'use client';

import { useState, useEffect } from 'react';
import { 
  Card, 
  Text, 
  Badge, 
  Button, 
  Group, 
  Stack,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Collapse,
  Code,
  JsonInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconPlus, 
  IconBrain, 
  IconGitBranch, 
  IconFolder,
  IconChevronDown,
  IconChevronRight,
  IconRocket,
  IconAnalyze,
  IconEdit,
  IconTrash,
  IconTarget,
  IconGitCommit,
  IconGitPullRequest,
  IconDownload,
  IconUpload,
  IconRefresh,
} from '@tabler/icons-react';
import { showLoading, showSuccess, showError } from '@/lib/toast';

interface Project {
  id: string;
  projectNo: number;
  name: string;
  description: string;
  goals: string;
  status: string;
  gitRepo?: string;
  gitBranch?: string;
  workingDir?: string;
  customPrompt?: string;
  references?: any[];
  createdAt: string;
  plans?: any[];
  _count?: { plans: number };
}

interface GitStatus {
  configured: boolean;
  workingDir?: string;
  gitRepo?: string;
  gitBranch?: string;
  currentBranch?: string;
  hasChanges?: boolean;
  status?: string;
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [aiCreateModalOpened, { open: openAiCreate, close: closeAiCreate }] = useDisclosure(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [gitStatuses, setGitStatuses] = useState<Record<string, GitStatus>>({});
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goals: '',
    gitRepo: '',
    gitBranch: 'main',
    workingDir: '',
    customPrompt: '',
    references: '[]',
  });
  
  const [aiDescription, setAiDescription] = useState('');
  const [autoExecute, setAutoExecute] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await fetch('/api/projects');
      const data = await res.json();
      
      // Load git status for projects with git repos
      for (const project of data.projects || []) {
        if (project.gitRepo) {
          loadGitStatus(project.id);
        }
      }
    } catch (error) {
      showError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadGitStatus = async (projectId: string) => {
    try {
      const res = await fetch(`/api/projects/${projectId}/git`);
      const data = await res.json();
      setGitStatuses(prev => ({
        ...prev,
        [projectId]: data,
      }));
    } catch (error) {
      console.error('Failed to load git status:', error);
    }
  };

  const createProject = async () => {
    const toastId = showLoading('Creating project...');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          references: JSON.parse(formData.references || '[]'),
        }),
      });
      
      if (!res.ok) throw new Error('Failed to create');
      
      showSuccess('Project created successfully', toastId);
      closeCreate();
      loadProjects();
      resetForm();
    } catch (error) {
      showError('Failed to create project', toastId);
    }
  };

  const createAiProject = async () => {
    const toastId = showLoading('AI is creating project...');
    try {
      const res = await fetch('/api/projects/ai/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: aiDescription,
          auto: autoExecute,
        }),
      });
      
      if (!res.ok) throw new Error('Failed to create');
      
      const data = await res.json();
      showSuccess(
        autoExecute 
          ? 'Project created and planning started!' 
          : 'Project created by AI!',
        toastId
      );
      closeAiCreate();
      loadProjects();
      setAiDescription('');
    } catch (error) {
      showError('AI project creation failed', toastId);
    }
  };

  const generatePlan = async (projectId: string) => {
    const toastId = showLoading('Generating plan...');
    try {
      const res = await fetch(`/api/projects/${projectId}/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate-plan' }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      showSuccess('Plan generation started', toastId);
      loadProjects();
    } catch (error) {
      showError('Failed to generate plan', toastId);
    }
  };

  const analyzeProject = async (projectId: string) => {
    const toastId = showLoading('Analyzing project...');
    try {
      const res = await fetch(`/api/projects/${projectId}/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'analyze-project' }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      showSuccess('Project analysis started', toastId);
    } catch (error) {
      showError('Analysis failed', toastId);
    }
  };

  const gitSetup = async (projectId: string) => {
    const toastId = showLoading('Setting up git repository...');
    try {
      const res = await fetch(`/api/projects/${projectId}/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setup' }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      showSuccess('Git repository setup complete', toastId);
      loadGitStatus(projectId);
      loadProjects();
    } catch (error) {
      showError('Git setup failed', toastId);
    }
  };

  const gitPull = async (projectId: string) => {
    const toastId = showLoading('Pulling changes...');
    try {
      const res = await fetch(`/api/projects/${projectId}/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'pull' }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      showSuccess('Changes pulled successfully', toastId);
      loadGitStatus(projectId);
    } catch (error) {
      showError('Git pull failed', toastId);
    }
  };

  const gitCommitAndPush = async (projectId: string) => {
    const toastId = showLoading('Committing and pushing...');
    try {
      const res = await fetch(`/api/projects/${projectId}/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'commit-and-push',
          message: 'Auto commit from AI Agent'
        }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      const data = await res.json();
      if (data.commit?.success && data.push?.success) {
        showSuccess('Changes committed and pushed!', toastId);
      } else if (data.commit?.success) {
        showSuccess('Changes committed (nothing to push)', toastId);
      } else {
        showError('Commit/push failed', toastId);
      }
      
      loadGitStatus(projectId);
    } catch (error) {
      showError('Git commit/push failed', toastId);
    }
  };

  const autoExecuteProject = async (projectId: string) => {
    const toastId = showLoading('Starting autonomous execution...');
    try {
      const res = await fetch(`/api/projects/${projectId}/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'auto-execute' }),
      });
      
      if (!res.ok) throw new Error('Failed');
      
      showSuccess('Autonomous execution started!', toastId);
      loadProjects();
    } catch (error) {
      showError('Auto-execute failed', toastId);
    }
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      goals: '',
      gitRepo: '',
      gitBranch: 'main',
      workingDir: '',
      customPrompt: '',
      references: '[]',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'blue';
      case 'paused': return 'yellow';
      case 'completed': return 'green';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  if (loading) {
    return <Text>Loading projects...</Text>;
  }

  return (
    <div>
      <Group justify="space-between" mb="xl">
        <div>
          <Text size="xl" fw={700}>Projects</Text>
          <Text size="sm" c="dimmed">Manage your AI-powered projects</Text>
        </div>
        <Group>
          <Button 
            leftSection={<IconBrain size={16} />}
            onClick={openAiCreate}
            variant="gradient"
            gradient={{ from: 'violet', to: 'grape' }}
          >
            AI Create Project
          </Button>
          <Button 
            leftSection={<IconPlus size={16} />}
            onClick={openCreate}
          >
            New Project
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        {projects.map((project) => (
          <Card key={project.id} shadow="sm" padding="lg" withBorder>
            <Group justify="space-between" mb="sm">
              <Group>
                <ActionIcon 
                  variant="subtle" 
                  onClick={() => toggleProject(project.id)}
                >
                  {expandedProjects.has(project.id) ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                </ActionIcon>
                <div>
                  <Group gap="xs">
                    <Text fw={600} size="lg">{project.name}</Text>
                    <Badge color={getStatusColor(project.status)} size="sm">
                      {project.status}
                    </Badge>
                    <Badge variant="light" size="sm">
                      #{project.projectNo}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">{project.description}</Text>
                </div>
              </Group>
              
              <Group>
                <Button 
                  size="xs" 
                  variant="light"
                  leftSection={<IconTarget size={14} />}
                  onClick={() => generatePlan(project.id)}
                >
                  Generate Plan
                </Button>
                <Button 
                  size="xs" 
                  variant="light"
                  color="violet"
                  leftSection={<IconAnalyze size={14} />}
                  onClick={() => analyzeProject(project.id)}
                >
                  Analyze
                </Button>
                <Button 
                  size="xs" 
                  variant="gradient"
                  gradient={{ from: 'violet', to: 'grape' }}
                  leftSection={<IconRocket size={14} />}
                  onClick={() => autoExecuteProject(project.id)}
                >
                  Auto Execute
                </Button>
              </Group>
            </Group>

            <Collapse in={expandedProjects.has(project.id)}>
              <Stack gap="sm" mt="md">
                {project.goals && (
                  <div>
                    <Text size="sm" fw={600} mb={4}>Goals:</Text>
                    <Text size="sm">{project.goals}</Text>
                  </div>
                )}
                
                <Group gap="xl">
                  {project.gitRepo && (
                    <Group gap={4}>
                      <IconGitBranch size={14} />
                      <Text size="xs" c="dimmed">{project.gitRepo}</Text>
                      {project.gitBranch && (
                        <Badge size="xs" variant="dot">{project.gitBranch}</Badge>
                      )}
                    </Group>
                  )}
                  {project.workingDir && (
                    <Group gap={4}>
                      <IconFolder size={14} />
                      <Text size="xs" c="dimmed">{project.workingDir}</Text>
                    </Group>
                  )}
                </Group>

                {/* Git Status & Actions */}
                {project.gitRepo && (
                  <Card withBorder padding="sm" bg="gray.0">
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" fw={600}>Git Status</Text>
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconRefresh size={12} />}
                          onClick={() => loadGitStatus(project.id)}
                        >
                          Refresh
                        </Button>
                      </Group>
                      
                      {gitStatuses[project.id] ? (
                        <>
                          {gitStatuses[project.id].configured ? (
                            <>
                              <Group gap="xs">
                                <Text size="xs" c="dimmed">Branch:</Text>
                                <Badge size="xs" variant="light">
                                  {gitStatuses[project.id].currentBranch || 'unknown'}
                                </Badge>
                                {gitStatuses[project.id].hasChanges && (
                                  <Badge size="xs" color="orange">
                                    Uncommitted changes
                                  </Badge>
                                )}
                              </Group>
                              
                              <Group gap="xs">
                                <Button
                                  size="xs"
                                  variant="light"
                                  leftSection={<IconDownload size={12} />}
                                  onClick={() => gitPull(project.id)}
                                >
                                  Pull
                                </Button>
                                <Button
                                  size="xs"
                                  variant="light"
                                  color="green"
                                  leftSection={<IconUpload size={12} />}
                                  onClick={() => gitCommitAndPush(project.id)}
                                  disabled={!gitStatuses[project.id].hasChanges}
                                >
                                  Commit & Push
                                </Button>
                              </Group>
                            </>
                          ) : (
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconGitCommit size={12} />}
                              onClick={() => gitSetup(project.id)}
                            >
                              Clone Repository
                            </Button>
                          )}
                        </>
                      ) : (
                        <Text size="xs" c="dimmed">Loading git status...</Text>
                      )}
                    </Stack>
                  </Card>
                )}

                {project.plans && project.plans.length > 0 && (
                  <div>
                    <Text size="sm" fw={600} mb={4}>
                      Plans ({project.plans.length}):
                    </Text>
                    <Stack gap={4}>
                      {project.plans.map((plan: any) => (
                        <Group key={plan.id} gap="xs">
                          <Badge size="sm" variant="dot" color={
                            plan.status === 'completed' ? 'green' :
                            plan.status === 'active' ? 'blue' : 'gray'
                          }>
                            {plan.status}
                          </Badge>
                          <Text size="sm">{plan.title}</Text>
                          <Text size="xs" c="dimmed">
                            ({plan.completedCount}/{plan.taskCount} tasks)
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </div>
                )}
              </Stack>
            </Collapse>
          </Card>
        ))}
        
        {projects.length === 0 && (
          <Card padding="xl" withBorder>
            <Stack align="center" gap="md">
              <Text c="dimmed">No projects yet</Text>
              <Button onClick={openAiCreate}>Create your first project with AI</Button>
            </Stack>
          </Card>
        )}
      </Stack>

      {/* Manual Create Modal */}
      <Modal 
        opened={createModalOpened} 
        onClose={closeCreate} 
        title="Create New Project"
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Project Name"
            placeholder="My Awesome Project"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Textarea
            label="Description"
            placeholder="Project description..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />
          <Textarea
            label="Goals"
            placeholder="What are the project objectives?"
            value={formData.goals}
            onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
            rows={3}
          />
          <TextInput
            label="Git Repository"
            placeholder="https://github.com/user/repo"
            value={formData.gitRepo}
            onChange={(e) => setFormData({ ...formData, gitRepo: e.target.value })}
          />
          <TextInput
            label="Git Branch"
            placeholder="main"
            value={formData.gitBranch}
            onChange={(e) => setFormData({ ...formData, gitBranch: e.target.value })}
          />
          <TextInput
            label="Working Directory"
            placeholder="/path/to/project"
            value={formData.workingDir}
            onChange={(e) => setFormData({ ...formData, workingDir: e.target.value })}
          />
          <Textarea
            label="Custom Prompt (Optional)"
            placeholder="Special instructions for AI agents..."
            value={formData.customPrompt}
            onChange={(e) => setFormData({ ...formData, customPrompt: e.target.value })}
            rows={2}
          />
          <JsonInput
            label="References (JSON array)"
            placeholder='["https://docs.example.com", "https://github.com/example"]'
            value={formData.references}
            onChange={(value) => setFormData({ ...formData, references: value })}
            rows={3}
          />
          
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
            <Button onClick={createProject}>Create Project</Button>
          </Group>
        </Stack>
      </Modal>

      {/* AI Create Modal */}
      <Modal 
        opened={aiCreateModalOpened} 
        onClose={closeAiCreate} 
        title="AI Project Creation"
        size="md"
      >
        <Stack gap="md">
          <Textarea
            label="Describe your project idea"
            placeholder="I want to build a web app that helps users track their daily habits..."
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            rows={6}
            required
          />
          
          <Select
            label="Mode"
            value={autoExecute ? 'auto' : 'manual'}
            onChange={(value) => setAutoExecute(value === 'auto')}
            data={[
              { value: 'manual', label: 'Manual - Just create project structure' },
              { value: 'auto', label: 'Auto - Create and start execution' },
            ]}
          />
          
          <Text size="xs" c="dimmed">
            AI will analyze your idea and create a structured project with goals, 
            recommended tools, and initial plans.
          </Text>
          
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeAiCreate}>Cancel</Button>
            <Button 
              onClick={createAiProject}
              leftSection={<IconBrain size={16} />}
              gradient={{ from: 'violet', to: 'grape' }}
              variant="gradient"
            >
              Let AI Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
